import { Loader2, RefreshCw, CheckCircle2, XCircle, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAdminSystem } from '@/api/hooks';
import { ModuleSettingsList } from './AdminModuleSettings';

function fmtUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
}

const ENV_VAR_LABELS: Record<string, string> = {
  AUTOTASK_USERNAME:          'Autotask username',
  AUTOTASK_SECRET:            'Autotask secret',
  AUTOTASK_INTEGRATION_CODE:  'Autotask integration code',
  SESSION_SECRET:             'Session secret',
  APP_URL:                    'App URL',
  AUTOTASK_WEBHOOK_SECRET:    'Webhook secret',
  MICROSOFT_CLIENT_ID:        'Microsoft client ID',
  MICROSOFT_CLIENT_SECRET:    'Microsoft client secret',
  MICROSOFT_TENANT_ID:        'Microsoft tenant ID',
  ADMIN_EMAIL:                'Admin email',
};

export default function AdminSystemPage() {
  const { data, isLoading, error, refetch, isFetching } = useAdminSystem();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">System</h1>
        <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load system info'}
        </div>
      )}

      {data && (
        <div className="space-y-5">
          {/* Runtime */}
          <section>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Runtime</h2>
            <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm divide-y divide-divider">
              <InfoRow label="Uptime">{fmtUptime(data.uptimeSeconds)}</InfoRow>
              <InfoRow label="Node.js">{data.nodeVersion}</InfoRow>
              <InfoRow label="Environment">
                <span className={data.environment === 'production' ? 'text-success font-medium' : 'text-warning font-medium'}>
                  {data.environment}
                </span>
              </InfoRow>
              <InfoRow label="SSE connections">
                <span className="flex items-center gap-1.5">
                  <Wifi className="h-3.5 w-3.5 text-muted-foreground" />
                  {data.sseClients} browser{data.sseClients !== 1 ? 's' : ''} connected
                </span>
              </InfoRow>
            </div>
          </section>

          {/* Env vars */}
          <section>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Environment Variables</h2>
            <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm divide-y divide-divider">
              {Object.entries(data.envVarStatus).map(([key, set]) => (
                <InfoRow key={key} label={ENV_VAR_LABELS[key] ?? key}>
                  <span className={`flex items-center gap-1.5 text-xs font-medium ${set ? 'text-success' : 'text-destructive'}`}>
                    {set
                      ? <><CheckCircle2 className="h-3.5 w-3.5" />Set</>
                      : <><XCircle className="h-3.5 w-3.5" />Missing</>}
                  </span>
                </InfoRow>
              ))}
            </div>
          </section>
        </div>
      )}

      <section>
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Settings</h2>
        <p className="mb-3 text-xs text-muted-foreground">Cross-module settings. Module-specific ones live under each module in the Settings menu.</p>
        <ModuleSettingsList module="system" />
      </section>
    </div>
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <span className="w-44 shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 text-sm">{children}</span>
    </div>
  );
}
